import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { useAuth } from './AuthContext';
import { PlanLimits, SubscriptionPlan, FREE_LIMITS, PRO_LIMITS } from '../domain/models';
import {
  getSubscriptionUseCase,
  checkFeatureAccessUseCase,
} from '../core/dependencies';
import {
  configureRevenueCat,
  loginRevenueCat,
  logoutRevenueCat,
  checkEntitlements,
} from '../core/config/revenuecat';
import { logger } from '@/src/core/utils';
import { FeatureCheck, FeatureAccessResult } from '../domain/usecases/CheckFeatureAccessUseCase';

interface SubscriptionContextType {
  isPro: boolean;
  plan: SubscriptionPlan;
  limits: PlanLimits;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  checkAccess: (check: FeatureCheck) => Promise<FeatureAccessResult>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState<SubscriptionPlan>('free');
  const [loading, setLoading] = useState(true);

  const isPro = plan === 'pro';
  const limits = isPro ? PRO_LIMITS : FREE_LIMITS;

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setPlan('free');
      setLoading(false);
      return;
    }

    try {
      // Check Supabase subscription record first
      const subscription = await getSubscriptionUseCase.execute(user.id);

      if (subscription?.plan === 'pro') {
        setPlan('pro');
      } else {
        // Fallback: check RevenueCat entitlements directly
        const hasProEntitlement = await checkEntitlements();
        setPlan(hasProEntitlement ? 'pro' : 'free');
      }
    } catch (error) {
      logger.error('Subscription', 'Failed to refresh subscription', { error });
      setPlan('free');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const checkAccess = useCallback(async (check: FeatureCheck): Promise<FeatureAccessResult> => {
    if (!user) return { allowed: false, reason: 'not_authenticated' };
    return checkFeatureAccessUseCase.execute(user.id, check);
  }, [user]);

  useEffect(() => {
    const init = async () => {
      // Configure RevenueCat SDK
      await configureRevenueCat(user?.id);

      if (user) {
        await loginRevenueCat(user.id);
      }

      await refreshSubscription();
    };

    init();
  }, [user, refreshSubscription]);

  useEffect(() => {
    if (!user) {
      logoutRevenueCat();
    }
  }, [user]);

  // Refresh when app returns to foreground so expired subscriptions are detected promptly
  const appState = useRef(AppState.currentState);
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState: AppStateStatus) => {
      if (appState.current.match(/inactive|background/) && nextState === 'active') {
        refreshSubscription();
      }
      appState.current = nextState;
    });
    return () => subscription.remove();
  }, [refreshSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        isPro,
        plan,
        limits,
        loading,
        refreshSubscription,
        checkAccess,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
